import { useState } from 'react';

const escapeHtml = (value = '') => String(value).replace(/[&<>'"]/g, (character) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', "'": '&#39;', '"': '&quot;' }[character]));

const openTrainingPrintView = ({ steps, institutionName, guideName, qrImageUrl }) => {
  const printWindow = window.open('', '_blank');
  if (!printWindow) return;
  printWindow.opener = null;
  const sections = steps.map((step) => `
    <article class="chapter">
      <h2>${escapeHtml(step.title.replace(/^\d+\.\s*/, ''))}</h2>
      ${step.intro ? `<p class="intro">${escapeHtml(step.intro)}</p>` : ''}
      ${(step.sections || []).map((section) => `
        <section class="section">
          <h3>${escapeHtml(section.heading)}</h3>
          ${section.body ? `<p>${escapeHtml(section.body)}</p>` : ''}
          ${section.bullets ? `<ul>${section.bullets.map((bullet) => `<li>${escapeHtml(bullet)}</li>`).join('')}</ul>` : ''}
          ${section.quote ? `<blockquote>${escapeHtml(section.quote)}</blockquote>` : ''}
        </section>`).join('')}
    </article>`).join('');
  printWindow.document.open();
  printWindow.document.write(`<!doctype html><html><head><title>${escapeHtml(guideName || 'GuideSofia staff training')}</title><style>
    @page { size: A4; margin: 18mm 16mm; }
    * { box-sizing: border-box; } body { margin: 0; color: #17212b; font-family: Arial, sans-serif; font-size: 11pt; line-height: 1.55; }
    .header { display: flex; align-items: center; justify-content: space-between; gap: 24px; padding-bottom: 18px; border-bottom: 3px solid #087b98; margin-bottom: 24px; }
    .brand { display: flex; align-items: center; gap: 14px; } .logo { width: 64px; height: 64px; object-fit: contain; } .qr { width: 110px; height: 110px; object-fit: contain; }
    h1 { margin: 0; font-size: 19pt; } .institution { margin-top: 4px; color: #087b98; font-weight: 700; font-size: 12pt; } .website { margin-top: 3px; color: #64748b; font-size: 9pt; }
    .chapter { break-inside: avoid; margin-bottom: 22px; } h2 { margin: 0 0 8px; padding-bottom: 5px; border-bottom: 1px solid #cbd5e1; color: #123f4c; font-size: 14pt; } h3 { margin: 13px 0 4px; font-size: 11pt; color: #334155; }
    p { margin: 5px 0; } .intro { color: #475569; } ul { margin: 5px 0 8px; padding-left: 23px; } li { margin: 3px 0; } blockquote { margin: 8px 0; padding: 8px 12px; border-left: 4px solid #22a6c3; background: #effaff; font-style: italic; }
  </style></head><body><header class="header"><div class="brand"><img class="logo" src="https://www.guidesofia.com/wp-content/uploads/2025/05/Circle-logo.png" alt="GuideSofia"><div><h1>Formação para o lançamento do guia digital</h1><div class="institution">${escapeHtml(institutionName || 'GuideSofia')}${guideName ? ` — ${escapeHtml(guideName)}` : ''}</div><div class="website">www.guidesofia.com</div></div></div>${qrImageUrl ? `<img class="qr" src="${escapeHtml(qrImageUrl)}" alt="QR code do guia">` : ''}</header>${sections}</body></html>`);
  printWindow.onload = () => {
    printWindow.focus();
    printWindow.print();
  };
  printWindow.document.close();
};

const StaffTrainingGuide = ({ steps = [], institutionName = '', guideName = '', qrImageUrl = '' }) => {
  const [openItems, setOpenItems] = useState({ 0: true });

  return (
    <div className="grid gap-4 lg:col-span-2">
      <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-cyan-200 bg-cyan-50 p-4 print:hidden">
        <div>
          <div className="text-sm font-semibold text-cyan-950">Guia de formação para a equipa</div>
          <p className="mt-1 text-sm text-cyan-900">Use as secções abaixo durante o briefing e abra-as com a equipa.</p>
        </div>
        <button type="button" onClick={() => openTrainingPrintView({ steps, institutionName, guideName, qrImageUrl })} className="rounded-lg bg-cyan-700 px-4 py-2 text-sm font-semibold text-white hover:bg-cyan-800">Abrir guia para guardar em PDF</button>
      </div>

      <div className="hidden items-center justify-between gap-8 border-b-2 border-cyan-700 pb-5 print:flex">
        <div className="flex items-center gap-4">
          <img src="https://www.guidesofia.com/wp-content/uploads/2025/05/Circle-logo.png" alt="GuideSofia" className="h-16 w-16 object-contain" />
          <div>
            <div className="text-xl font-bold text-slate-900">Formação para o lançamento do guia digital</div>
            <div className="mt-1 text-base font-semibold text-cyan-800">{institutionName || 'GuideSofia'}{guideName ? ` — ${guideName}` : ''}</div>
            <div className="mt-1 text-xs text-slate-500">www.guidesofia.com</div>
          </div>
        </div>
        {qrImageUrl && <img src={qrImageUrl} alt="QR code do guia" className="h-28 w-28 object-contain" />}
      </div>

      {steps.map((step, index) => {
        const isOpen = Boolean(openItems[index]);
        return (
          <article key={step.title} className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm print:break-inside-avoid print:border-0 print:shadow-none">
            <button type="button" onClick={() => setOpenItems((current) => ({ ...current, [index]: !isOpen }))} className="flex w-full items-center gap-3 p-5 text-left print:cursor-default">
              <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-cyan-100 text-sm font-semibold text-cyan-900">{index + 1}</span>
              <span className="flex-1 text-base font-semibold text-slate-900">{step.title.replace(/^\d+\.\s*/, '')}</span>
              <span className="text-xl text-slate-400 print:hidden">{isOpen ? '−' : '+'}</span>
            </button>
            <div className={`${isOpen ? 'block' : 'hidden'} border-t border-slate-200 px-5 pb-5 pt-4 print:block`}>
              {step.intro && <p className="mb-5 text-sm leading-7 text-slate-700">{step.intro}</p>}
              <div className="grid gap-5">
                {step.sections?.map((section) => (
                  <div key={section.heading}>
                    <h3 className="text-sm font-semibold text-slate-900">{section.heading}</h3>
                    {section.body && <p className="mt-2 text-sm leading-7 text-slate-700">{section.body}</p>}
                    {section.bullets && <ul className="mt-2 list-disc space-y-2 pl-6 text-sm leading-7 text-slate-700">{section.bullets.map((bullet) => <li key={bullet}>{bullet}</li>)}</ul>}
                    {section.quote && <blockquote className="mt-3 rounded-lg border-l-4 border-cyan-400 bg-cyan-50 px-4 py-3 text-sm italic leading-7 text-cyan-950">{section.quote}</blockquote>}
                  </div>
                ))}
              </div>
            </div>
          </article>
        );
      })}
    </div>
  );
};

export default StaffTrainingGuide;
